"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OverlayWithSpinner = exports.DataGridWithSynchronousRowStore = void 0;
const tslib_1 = require("tslib");
const vizportalclient_common_components_1 = require("@tableau/vizportalclient-common-components");
const widgets_datagrid_1 = require("@tableau/widgets-datagrid");
const react_1 = tslib_1.__importDefault(require("react"));
const reselect_1 = require("reselect");
const shallowequal_1 = tslib_1.__importDefault(require("shallowequal"));
const CenteredSpinner_1 = require("../CenteredSpinner");
const DataGridStyles_1 = require("../style/DataGridStyles");
const ownPropsNamesObj = {
    showSpinnerWhileLoading: null,
    onError: null,
};
/**
 * This component transforms the asynchronous callback-based API of the Data Grid's RowStore into a
 * "synchronous" pattern that more closely aligns with React idioms, storing the returned rows in
 * component state and re-rendering the data grid every time the stored rows are updated.
 *
 * This was originally built as a workaround for a race condition defect in the data grid (TFS 894890):
 * The data grid doesn't render measured column widths correctly with an async row store, so we
 * avoid that faulty code path by giving the data grid a row store that always returns rows synchronously.
 *
 * HOWEVER, this component now contains more functionality than simply the adapter for the RowStore.
 * It now also keeps track of loading and error states from the row store query.
 */
class DataGridWithSynchronousRowStore extends react_1.default.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loadingFromNewRowStore: true,
            currentlyStoredRows: [],
        };
        this.previouslyReportedErrors = [];
        // Keep track of the component being mounted, to avoid calling setState after unmount
        // internalIsMounted must start out as true so that the the first rowHandler callback will update
        // the state, if the backing row store happens to be synchronous.
        this.internalIsMounted = true;
        this.componentDidMount = () => this.internalIsMounted = true;
        this.componentWillUnmount = () => this.internalIsMounted = false;
        // This is the getItems function that is passed as the "row store" to the actual datagrid
        this.rowStoreGetItems = (start, end, rowHandler) => {
            this.fetchRowsAsynchronously(start, end);
            rowHandler(this.state.currentlyStoredRows);
        };
        this.fetchRowsAsynchronously = (start, end) => {
            const rowStore = this.props.rowStore;
            rowStore.getItems(start, end, (newRows, newErrors) => {
                if (!this.internalIsMounted || rowStore !== this.props.rowStore) {
                    return;
                }
                // Notify parent of any errors that came back. We do this here, rather than in componentDidUpdate,
                // because the latter causes the data grid not to apply the column widths.
                const hasNewErrors = newErrors && newErrors.length > 0 && !(0, shallowequal_1.default)(newErrors, this.previouslyReportedErrors);
                if (hasNewErrors) {
                    this.reportNewErrors(newRows, newErrors);
                    // Notifying the parent component may cause this component to become unmounted
                    if (!this.internalIsMounted) {
                        return;
                    }
                }
                this.setState(oldState => {
                    // If we don't check for equality, we'll get stuck in an infinite loop of re-rendering the same rows.
                    // Since our PaginatedRowStore shallow-copies the data every time it returns the row array, we have to use
                    // a shallowEqual check, rather than simply reference equality.
                    const rowsHaveChanged = !(0, shallowequal_1.default)(oldState.currentlyStoredRows, newRows);
                    if (rowsHaveChanged || hasNewErrors) {
                        return {
                            loadingFromNewRowStore: false,
                            currentlyStoredRows: rowsHaveChanged ? newRows : oldState.currentlyStoredRows,
                        };
                    }
                    else {
                        return null;
                    }
                });
            });
        };
        // Create a new row store only when the rows change. Creating a new row store will force the data grid to re-fetch,
        // so we want to do this only when there is actually new data to re-fetch (e.g. when the "async" fetch has completed)
        // but not more frequently (e.g. when the component gets re-rendered from above with no new props)
        this.memoizedCreateRowStore = (0, reselect_1.defaultMemoize)((_rows) => {
            return { getItems: this.rowStoreGetItems };
        });
    }
    reportNewErrors(newRows, newErrors) {
        this.previouslyReportedErrors = newErrors;
        this.props.onError(newRows, newErrors);
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        // When the row store from above has changed, we keep the old rows, but set the loading boolean to true.
        // This will cause the component to render a spinner but keep the old rows visible until the new rows arrive.
        if (this.props.rowStore !== nextProps.rowStore) {
            this.setState({
                loadingFromNewRowStore: true,
            });
            // Clear out the stored errors in ExplorerStyledDisplayGrid so they will be un-rendered
            // and new errors from the new row store can take their place. This is necessary because
            // there are two sources of state for errors: the rendered ErrorReporter objects and the error list
            // inside ErrorListProvider. In order to remove the errors from the ErrorListProvider, we unmount
            // the errors that are rendered by ExplorerStyledDisplayGrid, thus unregistering them from the error list.
            this.props.onError([], []);
        }
    }
    getDataGridPropsOnly() {
        const allProps = Object.assign({}, this.props);
        for (const key in ownPropsNamesObj) {
            if (key in allProps) {
                delete allProps[key];
            }
        }
        return allProps;
    }
    render() {
        const dataGridPropsWithOverrides = Object.assign(Object.assign({}, this.getDataGridPropsOnly()), { rowStore: this.memoizedCreateRowStore(this.props.rowStore, this.state.currentlyStoredRows), config: Object.assign(Object.assign({}, this.props.config), { rememberLastScrollPos: true }) });
        const displayLoadingSpinner = this.props.showSpinnerWhileLoading && this.state.loadingFromNewRowStore;
        return (react_1.default.createElement(vizportalclient_common_components_1.SuperimposedDivs, null,
            react_1.default.createElement(widgets_datagrid_1.DataGrid, Object.assign({}, dataGridPropsWithOverrides, { key: 'DataGrid', "data-tb-test-id": 'grid', "aria-label": this.props.ariaLabel ? this.props.ariaLabel : '' })),
            displayLoadingSpinner ? react_1.default.createElement(OverlayWithSpinner, { key: 'Spinner' }) : null));
    }
}
exports.DataGridWithSynchronousRowStore = DataGridWithSynchronousRowStore;
function OverlayWithSpinner() {
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(DataGridStyles_1.DataGridSpinnerGlass, null),
        react_1.default.createElement("div", { "data-tb-test-id": 'overlay-with-spinner', style: { paddingTop: 48 } },
            react_1.default.createElement(CenteredSpinner_1.CenteredSpinner, null))));
}
exports.OverlayWithSpinner = OverlayWithSpinner;
//# sourceMappingURL=DataGridWithSynchronousRowStore.js.map